// Collection is setup, not the job — it sits below the analysis, collapsed by
// default. One report per product against a quota of roughly one a minute, so
// products are chosen deliberately rather than swept.
import React, { useEffect, useRef, useState } from 'react';
import { FiRefreshCw } from 'react-icons/fi';
import SelectProductCatalogueModal from '../../../../components/UI/SelectProductCatalogueModal';
import s from '../SearchTerms.module.css';
import { getCatalogue, getCollection, getMarketStatus, startAutoCollection, startCollection,
         startMarketPull, startSalesTrafficPull } from '../api';

const MAX_ASINS = 20;
const POLL_MS = 15000;
const BADGE = { complete: s.up, partial: s.down, failed: s.down };

export default function CollectPanel({ token, period, onPeriodChange, onCollected, onError }) {
  const [catalogue, setCatalogue] = useState([]);
  const [picker, setPicker] = useState(false);
  const [selected, setSelected] = useState([]);
  const [windowStart, setWindowStart] = useState('');
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [autoBusy, setAutoBusy] = useState(false);
  const [plan, setPlan] = useState(null);
  const [market, setMarket] = useState(null);
  const [marketBusy, setMarketBusy] = useState(false);
  const [trafficBusy, setTrafficBusy] = useState(false);
  const poll = useRef(null);

  const refresh = async () => {
    try {
      setStatus(await getCollection(token, { period }));
    } catch (e) { onError(e); }
    getMarketStatus(token, { period }).then((r) => setMarket(r.market)).catch(() => {});
  };

  // The marketplace report is one request for the whole marketplace, not one
  // per product, so it is a separate action from collecting your own products
  // — and it is what "Not ranking" and "Competitor wins" read.
  const runMarket = async () => {
    setMarketBusy(true);
    try {
      const chosen = status?.available_windows?.find((w) => w.start_date === windowStart);
      await startMarketPull(token, {
        period,
        ...(chosen ? { start_date: chosen.start_date, end_date: chosen.end_date } : {}),
      });
      await refresh();
    } catch (e) { onError(e); } finally { setMarketBusy(false); }
  };

  useEffect(() => { getCatalogue(token).then(setCatalogue).catch(onError); }, [token, onError]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refresh(); }, [period]);

  // Poll only while Amazon is still generating, and always clear on unmount.
  useEffect(() => {
    if (status?.status !== 'running') { clearInterval(poll.current); return undefined; }
    poll.current = setInterval(async () => {
      const next = await getCollection(token, { period }).catch(() => null);
      if (next) {
        setStatus(next);
        if (next.status !== 'running') onCollected?.();
      }
    }, POLL_MS);
    return () => clearInterval(poll.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status?.status, period]);

  // Amazon picks the products, not the seller. One catalogue-wide request
  // names everything with impressions; a product with none has no search
  // terms either, so collecting it spends a minute of quota on an empty report.
  const runAuto = async () => {
    setAutoBusy(true);
    setPlan(null);
    try {
      const chosen = status?.available_windows?.find((w) => w.start_date === windowStart);
      const res = await startAutoCollection(token, {
        period,
        ...(chosen ? { startDate: chosen.start_date, endDate: chosen.end_date } : {}),
      });
      setPlan(res.plan || null);
      await refresh();
    } catch (e) { onError(e); } finally { setAutoBusy(false); }
  };

  // Sales & Traffic is not a Brand Analytics report — every seller is served
  // it — so it works even on accounts where search query data is refused, and
  // it is the only source of a product's total revenue and total sessions.
  const runTraffic = async () => {
    setTrafficBusy(true);
    try {
      const chosen = status?.available_windows?.find((w) => w.start_date === windowStart);
      await startSalesTrafficPull(token, {
        period,
        ...(chosen ? { start_date: chosen.start_date, end_date: chosen.end_date } : {}),
      });
      await refresh();
    } catch (e) { onError(e); } finally { setTrafficBusy(false); }
  };

  const run = async () => {
    setBusy(true);
    try {
      const chosen = status?.available_windows?.find((w) => w.start_date === windowStart);
      await startCollection(token, {
        period, asins: selected,
        ...(chosen ? { start_date: chosen.start_date, end_date: chosen.end_date } : {}),
      });
      await refresh();
    } catch (e) { onError(e); } finally { setBusy(false); }
  };

  const jobs = status?.jobs || [];
  const progress = status?.progress;

  return (
    <section className={s.panel}>
      <h2 className={s.panelTitle}>Collect from Amazon</h2>
      <p className={s.hint}>
        <strong>Collect automatically</strong> asks Amazon which products actually received search
        impressions — one request for the whole catalogue — and then collects only those. Products
        with no impressions have no search terms, so collecting them costs a minute of quota each
        and returns nothing. Choose products by hand only when you want a specific one.
      </p>

      <div className={s.controls} style={{ marginTop: 14 }}>
        <label className={s.field}>
          <span className={s.label}>Period</span>
          <select className={s.select} value={period}
                  onChange={(e) => { onPeriodChange(e.target.value); setWindowStart(''); }}>
            {['WEEK', 'MONTH', 'QUARTER'].map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </label>

        <label className={s.field}>
          <span className={s.label}>Window</span>
          <select className={s.select} value={windowStart}
                  onChange={(e) => setWindowStart(e.target.value)}>
            <option value="">Last complete {period.toLowerCase()}</option>
            {(status?.available_windows || []).map((w) => (
              <option key={w.start_date} value={w.start_date}>
                {w.start_date} → {w.end_date}
              </option>
            ))}
          </select>
        </label>

        <div className={s.field} style={{ flex: 1, minWidth: 210 }}>
          <span className={s.label}>Products (max {MAX_ASINS})</span>
          <button className={s.secondary} style={{ width: '100%', textAlign: 'left' }}
                  onClick={() => setPicker(true)} disabled={!catalogue.length}>
            {selected.length
              ? `${selected.length} selected`
              : `Choose from ${catalogue.length || '…'} products`}
          </button>
        </div>

        <button className={s.button} onClick={runAuto} disabled={autoBusy || busy}>
          {autoBusy ? 'Asking Amazon…' : 'Collect automatically'}
        </button>
        <button className={s.secondary} onClick={run} disabled={busy || autoBusy || !selected.length}>
          {busy ? 'Requesting…' : `Collect chosen${selected.length ? ` (${selected.length})` : ''}`}
        </button>
        <button className={s.secondary} onClick={refresh}>
          <FiRefreshCw size={12} /> Refresh
        </button>
      </div>

      <div className={s.section} style={{ marginTop: 16 }}>
        <div className={s.sectionTitle}>Marketplace search terms</div>
        <p className={s.hint}>
          Amazon&apos;s own top search terms for the marketplace — what shoppers search and
          which products win those searches. It is one request for every seller, not one per
          product, and it is what <strong>Not ranking</strong> and{' '}
          <strong>Competitor wins</strong> read. Without it those two tabs stay empty.
        </p>
        <div className={s.controls} style={{ marginTop: 10 }}>
          <button className={s.secondary} onClick={runMarket}
                  disabled={marketBusy || market?.job?.status === 'requested'}>
            {marketBusy ? 'Requesting…'
              : market?.job?.status === 'requested' ? 'Amazon is building it…'
              : market?.collected ? 'Refresh marketplace terms' : 'Collect marketplace terms'}
          </button>
          <span className={s.hint}>
            {market?.job?.status === 'requested'
              ? 'Requested. This report is large and takes several minutes; it lands on the next refresh.'
              : market?.covers_your_window
                ? `Collected for ${market.latest} — covers the window you are viewing.`
                : market?.collected
                  ? `Collected for ${market.latest}, which is not the window you are viewing.`
                  : 'Not collected yet.'}
            {market?.job?.error ? ` Amazon said: ${market.job.error}` : ''}
          </span>
        </div>
      </div>

      <div className={s.section} style={{ marginTop: 16 }}>
        <div className={s.sectionTitle}>Sales &amp; Traffic</div>
        <p className={s.hint}>
          Each product&apos;s total revenue and total sessions. Brand Analytics says what
          search did; only this says what the product did altogether, so{' '}
          <strong>Revenue from search</strong> needs it to work out a share. Amazon serves
          this report to every seller, Brand Registry or not.
        </p>
        <div className={s.controls} style={{ marginTop: 10 }}>
          <button className={s.secondary} onClick={runTraffic}
                  disabled={trafficBusy || status?.sales_traffic?.status === 'requested'}>
            {trafficBusy ? 'Requesting…'
              : status?.sales_traffic?.status === 'requested' ? 'Amazon is building it…'
              : 'Collect sales & traffic'}
          </button>
          <span className={s.hint}>
            {status?.sales_traffic?.status === 'requested'
              ? 'Requested. It lands on the next refresh.'
              : status?.sales_traffic?.products
                ? `Collected — ${status.sales_traffic.products} products for `
                  + `${status.sales_traffic.start_date}.`
                : status?.sales_traffic?.error
                  ? `Amazon said: ${status.sales_traffic.error}`
                  : 'Not collected yet.'}
          </span>
        </div>
      </div>

      {!!selected.length && (
        <div className={s.chipRow} style={{ marginTop: 10 }}>
          {selected.map((asin) => (
            <span key={asin} className={s.chip}>
              {asin}
              <button className={s.chipRemove}
                      onClick={() => setSelected(selected.filter((a) => a !== asin))}>×</button>
            </span>
          ))}
          <button className={s.link} onClick={() => setSelected([])}>Clear</button>
        </div>
      )}

      {plan && (
        <p className={s.hint} style={{ marginTop: 12 }}>
          Amazon reported on {plan.products_in_report} product
          {plan.products_in_report === 1 ? '' : 's'} for {plan.start_date} → {plan.end_date}.
          {' '}<strong>{plan.products_with_impressions}</strong> received search impressions;
          collecting the top {plan.selected.length}. The rest have no search terms to fetch.
        </p>
      )}

      {status && status.status !== 'idle' && (
        <>
          <p className={s.hint} style={{ marginTop: 12 }}>
            <span className={BADGE[status.status] || ''}>{status.status}</span>
            {' · '}{progress?.done ?? 0} of {progress?.total ?? 0} reports
          </p>
          <div className={s.tableWrap} style={{ marginTop: 8 }}>
            <table className={s.table}>
              <thead>
                <tr>
                  <th>ASIN</th><th>Window</th><th>Status</th>
                  <th className={s.numeric}>Terms</th><th>Detail</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((j) => (
                  <tr key={`${j.asin}-${j.start_date}`}>
                    <td className={s.asin}>{j.asin}</td>
                    <td>{j.start_date} → {j.end_date}</td>
                    <td><span className={BADGE[j.status] || ''}>{j.status}</span></td>
                    <td className={s.numeric}>{j.terms || 0}</td>
                    <td className={s.muted}>{j.error || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <SelectProductCatalogueModal
        show={picker}
        products={catalogue}
        selectedAsins={selected}
        actionLabel="Use selected"
        onClose={() => setPicker(false)}
        onAsinSelect={(asin) => setSelected((cur) => {
          if (cur.includes(asin)) return cur.filter((a) => a !== asin);
          // Cap here rather than letting the request be rejected after the
          // seller has already chosen.
          return cur.length >= MAX_ASINS ? cur : [...cur, asin];
        })}
        // Select All fills up to the cap rather than doing nothing. It cannot
        // mean "all" here — the report costs one request per product — so it
        // takes the first MAX_ASINS and the chip count makes the limit obvious.
        selectAll={selected.length >= Math.min(MAX_ASINS, catalogue.length) && selected.length > 0}
        onSelectAll={() => setSelected((cur) =>
          cur.length ? [] : catalogue.slice(0, MAX_ASINS).map((p) => p.asin))}
        onAnalyze={() => setPicker(false)}
      />
    </section>
  );
}
